import { DownOutlined, ExportOutlined, MenuOutlined, QuestionCircleOutlined, YoutubeFilled } from "@ant-design/icons";
import { Button, Dropdown, Flex, Layout, type MenuProps, Segmented, Tooltip, message } from "antd";
import { isEmpty } from "lodash";
import React, { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import BannerStack from "../components/BannerStack/BannerStack";
import BillingInactiveModal from "../components/BillingInactiveModal/BillingInactiveModal";
import ChangePassword from "../components/ChangePassword/ChangePassword.component";
import { ViewType } from "../components/CreateNoteModal/AITemplateModal/types";
import ExtensionBanner from "../components/ExtensionBanner/ExtensionBanner.component";
import NudgeLogo from "../components/NudgeLogo";
import PurchaseModal from "../components/PurchaseModal/Purchase.component";
import Settings from "../components/Settings/Settings.component";
import ShareClientModal from "../components/ShareClientModal/ShareClientModal.component";
import TransferBanner from "../components/TransferBanner/TransferBanner";
import TransferModalsHost from "../components/TransferBanner/TransferModalsHost";
import { TransferProvider } from "../components/TransferButton/TransferContext";
import { isEligibleForOnboarding, isLockedOutOfApp } from "../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import { useIsMobileView } from "../hooks/ui-hook";
import useIntercomWithData from "../hooks/useIntercomWithData";
import {
  setNotesViewVisible,
  setOpenShare,
  setSelectedClient,
  setSelectedView,
  setShareClientModalClient,
  setShareClientModalVisible,
  setShowRestrictionModal,
  setShowSettingsModal,
} from "../slices/appStateSlice";
import { type UserInfoProps, UserTier, resetBasicUserInfo } from "../slices/authSlice";
import { fetchAllClients } from "../slices/clientSlice";
import { setNotesDetails } from "../slices/notesSlice";
import { setShowPaymentModal } from "../slices/userSlice";
import { EventType, trackEvent, trackServerEvent } from "../utils/analytics";
import { clearSessionAndRedirect } from "../utils/auth.utils";
import { LC_KEYS, STRIPE_BILLING_PORTAL_URL } from "../utils/constants";
import styles from "./layout.module.scss";

const { Header, Content } = Layout;

interface MenuItem {
  key: string;
  label: React.ReactNode;
  hide?: (userInfo: UserInfoProps) => boolean;
}

const AuthLayout = ({
  avoidTokenValidation = false,
}: {
  avoidTokenValidation?: boolean;
}) => {
  const component = "layout";
  //check for initiatePayment query param
  const searchParams = new URLSearchParams(window.location.search);
  const initiatePayment = searchParams.get("initiate_payment");
  const { bootWithData, shutdown, show: showIntercom } = useIntercomWithData();
  const userData = useAppSelector((state) => state.user.data);
  const { userInfo } = useAppSelector((state) => state.auth);
  const { isCustomiseSectionVisible, selectedView, isMobilePath, shareClientModalVisible, shareClientModalClient } =
    useAppSelector((state) => state.appState);
  const { isRecording, isPaused } = useAppSelector((state) => state.recordings);

  const location = useLocation();
  const navigation = useNavigate();
  const dispatch = useAppDispatch();
  const [openChangePasswordModal, setOpenChangePasswordModal] = React.useState<boolean>(false);
  const [isCompactView, setIsCompactView] = React.useState(window.innerWidth < 1200);
  const [isMediumBreakpoint, setIsMediumBreakpoint] = React.useState(
    window.innerWidth >= 768 && window.innerWidth <= 1200,
  );
  const isMobile = useIsMobileView();

  // Scroll the content region back to the top on every route change. The
  // scrollable element is <Content> (overflow:auto), not window — so navigating
  // into a clinician / finding / note kept the previous scroll position.
  const contentRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  // Keep the header segmented control in sync with the URL so the correct tab is
  // highlighted on direct load / refresh / back-forward — not only on click.
  // (Dashboard + Template Gallery both live under the "Clinic" tab.)
  React.useEffect(() => {
    const path = location.pathname;
    let view: ViewType | null = null;
    if (path.startsWith("/compliance")) view = ViewType.COMPLIANCE;
    else if (path.startsWith("/call/dashboard")) view = ViewType.AI_RECEPTIONIST;
    else if (path.startsWith("/templates")) view = ViewType.TEMPLATE;
    else if (path === "/home") view = ViewType.DASHBOARD;
    if (view && view !== selectedView) dispatch(setSelectedView(view));
  }, [location.pathname, selectedView, dispatch]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsCompactView(window.innerWidth < 1200);
      setIsMediumBreakpoint(window.innerWidth >= 768 && window.innerWidth <= 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isHeadlessHeader = location.pathname === "/onboarding";
  const segmentLess = location.pathname === "/tutorial" || location.pathname === "/onboarding";
  const onlyHeader = location.pathname === "/privacy" || location.pathname === "/terms";

  // Check if user needs onboarding
  const needsOnboarding = isEligibleForOnboarding(userInfo);

  const isLocked = isLockedOutOfApp(userInfo);

  const openShare = () => {
    trackEvent(EventType.SHARE_MODAL_OPEN, {
      shareModalType: "share_button",
    });
    trackServerEvent(EventType.SHARE_MODAL_OPEN, {
      shareModalType: "share_button",
    });
    dispatch(setOpenShare(true));
  };

  useEffect(() => {
    if (initiatePayment) {
      dispatch(setShowPaymentModal(true));
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [initiatePayment, dispatch]);

  useEffect(() => {
    // Redirect to home if trying to access onboarding when not needed
    if (location.pathname === "/onboarding" && !needsOnboarding) {
      navigation("/home");
      // Redirect to onboarding if needed
    } else if (location.pathname !== "/onboarding" && needsOnboarding) {
      navigation("/onboarding");
    }
  }, [location.pathname, needsOnboarding, navigation]);

  useEffect(() => {
    const isNonPayingUser = !isEmpty(userInfo) && !userInfo.is_paying;
    const isGroupUser = userInfo.group_name;
    if ((isNonPayingUser || isGroupUser) && !isCustomiseSectionVisible) {
      bootWithData();
    } else {
      shutdown();
    }
  }, [bootWithData, userInfo, shutdown, isCustomiseSectionVisible]);

  useEffect(() => {
    if (isLocked) {
      dispatch(setShowPaymentModal(true));
    }
  }, [isLocked]);

  const navigateToSubscription = () => {
    trackEvent(EventType.SUBSCRIBE_BUTTON);
    trackServerEvent(EventType.SUBSCRIBE_BUTTON);
    dispatch(setShowPaymentModal(true));
  };

  const logout = async () => {
    trackEvent(EventType.LOGOUT);
    await axiosInstance.post("/rest-auth/logout/", {}).catch((error) => {
      console.error(error);
    });
    dispatch(resetBasicUserInfo());
    clearSessionAndRedirect();
  };

  const navigateToHome = () => {
    navigation("/home");
    dispatch(setSelectedClient(null));
    dispatch(setNotesDetails(null));
    dispatch(setNotesViewVisible(false));
    dispatch(setSelectedView(ViewType.DASHBOARD));
  };

  const contactUs = () => {
    trackEvent(EventType.CONTACT_US);
    window.open("https://tally.so/r/wg5QxK", "_blank");
  };

  const openAnalyticsPage = () => {
    navigation("/analytics");
  };

  const openTemplateGallery = () => {
    if (isMobilePath) {
      dispatch(setShowRestrictionModal(true));
    } else {
      dispatch(setSelectedView(ViewType.TEMPLATE));
      navigation("/templates");
    }
  };

  const handleShareModalClose = () => {
    dispatch(setShareClientModalVisible(false));
    dispatch(setShareClientModalClient(null));
  };

  const shouldRenderLogo = userInfo.group_logo;

  const renderLogo = () => {
    const logoUrl = userInfo.group_logo as string;

    return (
      <Flex
        style={{
          padding: isMobile ? 2 : 4,
          backgroundColor: "#F9FAFB",
          height: "max-content",
          width: "max-content",
          borderRadius: 4,
          flexShrink: 0,
        }}
      >
        <img
          height={isMobile ? 24 : 36}
          width="auto"
          alt={userInfo.group_name}
          src={logoUrl}
          style={{ borderRadius: 4, maxWidth: isMobile ? 80 : 200, objectFit: "contain" }}
        />
      </Flex>
    );
  };

  const handleManageSubscription = async () => {
    const loading = message.loading("Fetching subscription details...");
    try {
      const response = await axiosInstance.post<{ portal_url: string }>("/users/billing-portal/");
      loading();
      window.open(response.data.portal_url, "_blank");
    } catch {
      loading();
      // Fallback: open static Stripe billing portal URL (requires email entry)
      window.open(STRIPE_BILLING_PORTAL_URL, "_blank");
    }
  };

  const menuItems: MenuItem[] = [
    {
      key: "profile",
      label: <div style={{ color: "#000" }}>{userData?.email || "User"}</div>,
    },
    {
      key: "new-features",
      label: (
        <a href="https://getnudgeai.com/release-notes" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          New Features <ExportOutlined />
        </a>
      ),
      hide: () => !isCompactView,
    },
    {
      key: "tutorials-header",
      label: (
        <a
          href="https://youtube.com/playlist?list=PL9lb9UfUiaJxz04wfQuQ0b8Khxln5HN-r&si=2tekWXkkfeoZIBnI"
          target="_blank"
          style={{ color: "#000" }}
          rel="noreferrer"
        >
          Tutorials <ExportOutlined />
        </a>
      ),
      hide: () => !isCompactView,
    },
    {
      key: "subscription",
      label: (
        <div style={{ color: "#000" }} onClick={navigateToSubscription}>
          Subscription
        </div>
      ),
      hide: (userInfo) => userInfo.is_paying,
    },
    {
      key: "settings",
      label: (
        <div style={{ color: "#000" }} onClick={() => dispatch(setShowSettingsModal(true))}>
          Settings
        </div>
      ),
    },
    {
      key: "my-account",
      label: (
        <div onClick={handleManageSubscription} style={{ color: "#000" }}>
          My Account
        </div>
      ),
      hide: (userInfo) => {
        if (userInfo.tier === UserTier.GROUP && userInfo.is_group_admin) {
          return false;
        }
        return !userInfo.is_paying;
      },
    },
    {
      key: "analytics",
      label: (
        <div onClick={openAnalyticsPage} style={{ color: "#000" }}>
          Analytics
        </div>
      ),
      hide: (userInfo) => !userInfo.enable_analytics,
    },
    {
      key: "manage-members",
      label: (
        <div onClick={() => navigation("/members")} style={{ color: "#000" }}>
          Manage Members
        </div>
      ),
      hide: (userInfo) => !userInfo.is_group_admin || !userInfo.is_member_management_enabled,
    },
    {
      key: "compliance",
      label: (
        <div
          onClick={() => {
            dispatch(setSelectedView(ViewType.COMPLIANCE));
            navigation("/compliance");
          }}
          style={{ color: "#000" }}
        >
          Compliance
        </div>
      ),
      // Compliance header shows to everyone (like Front-Desk) and routes to the
      // adoption landing page. show_compliance_dashboard only swaps that landing
      // page for the real dashboard with details — it does NOT hide the entry.
    },
    {
      key: "contact",
      label: (
        <div onClick={contactUs} style={{ color: "#000" }}>
          Contact us
        </div>
      ),
    },
    {
      key: "templates",
      label: (
        <div onClick={openTemplateGallery} style={{ color: "#000" }}>
          Template Gallery
        </div>
      ),
    },
    {
      key: "share",
      label: (
        <div onClick={openShare} style={{ color: "#000" }}>
          Share & Earn a Free Month
        </div>
      ),
    },
    {
      key: "consent-form",
      label: (
        <a href="/pdf/client_consent_form.pdf" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          Consent Form <ExportOutlined />
        </a>
      ),
    },
    {
      key: "tutorial",
      label: (
        <a
          href="https://www.youtube.com/playlist?list=PL9lb9UfUiaJwUUCOBLgiznYWj8NT-7jD2"
          target="_blank"
          style={{ color: "#000" }}
          rel="noreferrer"
        >
          Tutorial <ExportOutlined />
        </a>
      ),
    },
    {
      key: "ambassador",
      label: (
        <a href="https://tally.so/r/3xNd0G" target="_blank" style={{ color: "#000" }} rel="noreferrer">
          Become an Influencer <ExportOutlined />
        </a>
      ),
    },
    {
      key: "change-password",
      label: (
        <div onClick={() => setOpenChangePasswordModal(true)} style={{ color: "#000" }}>
          Change Password
        </div>
      ),
    },
    {
      key: "logout",
      label: (
        <div onClick={logout} style={{ color: "red" }}>
          Logout
        </div>
      ),
    },
  ];

  const setupSelectedView = (value: string) => {
    if (value === ViewType.CLINICAL_PRACTICE) {
      // Default subtab is Dashboard; user can pick Template Gallery from the
      // dropdown attached to the Clinical Practice label.
      dispatch(setNotesViewVisible(false));
      dispatch(setSelectedView(ViewType.DASHBOARD));
      navigation("/home");
      return;
    } else if (value === ViewType.TEMPLATE) {
      dispatch(setSelectedView(ViewType.TEMPLATE));
      navigation("/templates");
      return;
    } else if (value === ViewType.AI_RECEPTIONIST) {
      // Track event when Front-Desk tab is clicked
      trackEvent(EventType.AI_RECEPTIONIST_TAB_CLICKED, {
        tab_name: "Front-Desk",
        user_email: userData?.email,
      });
      dispatch(setSelectedView(ViewType.AI_RECEPTIONIST));
      navigation("/call/dashboard");
      return;
    } else if (value === ViewType.COMPLIANCE) {
      dispatch(setSelectedView(ViewType.COMPLIANCE));
      navigation("/compliance");
      return;
    }
    dispatch(setNotesViewVisible(false));
    dispatch(setSelectedView(ViewType.DASHBOARD));
    navigation("/home");
  };

  const filteredItems: MenuProps["items"] = menuItems
    .filter((item) => !item.hide || !item.hide(userInfo))
    .map(({ key, label }) => ({ key, label }));

  const lockedItems = filteredItems.filter((item) => item && item.key !== "templates" && item.key !== "share");

  const token = localStorage.getItem("token");

  // Build segmented options. Clinical Practice is a single top-level tab whose
  // label wraps a Dropdown — clicking opens a menu with the two subtabs
  // (Dashboard, Template Gallery). Front-Desk and Compliance both show to all
  // users: users without compliance provisioned land on the adoption page, while
  // enabled group admins get the dashboard (gated inside the /compliance route).
  const segmentedOptions = React.useMemo(() => {
    const clinicalPracticeMenu: MenuProps = {
      items: [
        {
          key: ViewType.DASHBOARD,
          label: "Dashboard",
          onClick: () => {
            dispatch(setNotesViewVisible(false));
            dispatch(setSelectedView(ViewType.DASHBOARD));
            navigation("/home");
          },
        },
        {
          key: ViewType.TEMPLATE,
          label: "Template Gallery",
          onClick: () => {
            dispatch(setSelectedView(ViewType.TEMPLATE));
            navigation("/templates");
          },
        },
      ],
    };

    const options: Array<{ label: React.ReactNode; value: string }> = [
      {
        label: (
          <Dropdown menu={clinicalPracticeMenu} trigger={["hover", "click"]}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {ViewType.CLINICAL_PRACTICE}
              <DownOutlined style={{ fontSize: 10 }} />
            </div>
          </Dropdown>
        ),
        value: ViewType.CLINICAL_PRACTICE,
      },
      {
        label: <div style={{ display: "flex", alignItems: "center", gap: 6 }}>{ViewType.AI_RECEPTIONIST}</div>,
        value: ViewType.AI_RECEPTIONIST,
      },
    ];
    // Compliance tab shows to everyone (like Front-Desk) so any group can reach
    // the adoption landing page. show_compliance_dashboard does NOT gate the tab;
    // it only decides whether /compliance renders the landing page (off) or the
    // real dashboard with details (on) — handled inside FindingsInbox.
    options.push({
      label: (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {ViewType.COMPLIANCE}
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 16,
              fontSize: 9,
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "0.06em",
              padding: "0 5px",
              borderRadius: 999,
              background: "#eef2ff",
              color: "#4338ca",
              textTransform: "uppercase",
            }}
          >
            Beta
          </span>
        </div>
      ),
      value: ViewType.COMPLIANCE,
    });
    return options;
  }, [dispatch, navigation]);

  if (!token && !avoidTokenValidation) {
    return <Navigate replace to={"/login"} />;
  }

  return (
    <TransferProvider>
      <Layout className={`${styles[`${component}__container`]}`}>
        {openChangePasswordModal && (
          <ChangePassword
            show={openChangePasswordModal}
            onClose={() => {
              setOpenChangePasswordModal(false);
            }}
          />
        )}
        {/* <OnboardingModal visible={location.pathname === "/onboarding" && isEligibleForOnboarding(userInfo)} /> */}
        <Header className={`${styles[`${component}__header`]}`}>
          {(() => {
            const proLabel = isMediumBreakpoint ? "Pro" : "Pro Tier";
            const groupLabel = userInfo.group_name || "Group Tier";
            const tierLabel =
              userInfo.tier === UserTier.PRO ? proLabel : userInfo.tier === UserTier.GROUP ? groupLabel : null;

            // Inline crown next to "nudge" on mobile (Pro only — Group keeps the badge below).
            // Hidden when shouldRenderLogo: a custom group logo replaces tier branding (matches existing badge-hiding logic).
            const mobileTierIcon =
              isMobile && !shouldRenderLogo && userInfo.tier === UserTier.PRO ? (
                <Tooltip title={tierLabel} trigger={["click", "hover"]}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      lineHeight: 0,
                    }}
                  >
                    <img src="/assets/crown-icon.png" alt="Pro" width={16} height={16} />
                  </span>
                </Tooltip>
              ) : null;

            return (
              <Flex align="center" gap={8} style={{ flex: 1 }}>
                <NudgeLogo
                  practiceArea={userInfo.practice_area}
                  onClick={navigateToHome}
                  showDomain={!shouldRenderLogo && location.pathname !== "/onboarding"}
                  compact={isMobile}
                  tierAdornment={mobileTierIcon}
                />
                {shouldRenderLogo ? (
                  renderLogo()
                ) : (
                  <>
                    {userInfo.tier === UserTier.PRO && !isMobile && (
                      <div
                        style={{
                          backgroundColor: "#F2F2F2",
                          borderRadius: 8,
                          padding: 8,
                          display: "flex",
                          alignItems: "center",
                          maxHeight: 44,
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            borderRadius: 6,
                            padding: "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            whiteSpace: "nowrap",
                            maxHeight: 28,
                          }}
                        >
                          <img src="/assets/crown-icon.png" alt="Pro" width={14} height={14} />
                          <span style={{ color: "#6B7280", fontSize: 14, fontWeight: 500 }}>{proLabel}</span>
                        </div>
                      </div>
                    )}
                    {userInfo.tier === UserTier.GROUP && (
                      <div
                        style={{
                          backgroundColor: "#F2F2F2",
                          borderRadius: isMobile ? 6 : 8,
                          padding: isMobile ? 3 : 8,
                          display: "flex",
                          alignItems: "center",
                          maxHeight: isMobile ? 28 : 44,
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "#FFFFFF",
                            borderRadius: isMobile ? 4 : 6,
                            padding: isMobile ? "2px 6px" : "4px 8px",
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap",
                            maxHeight: isMobile ? 22 : 28,
                          }}
                        >
                          <span
                            style={{
                              color: "#6B7280",
                              fontSize: isMobile ? 10 : 14,
                              fontWeight: 500,
                              maxWidth: isMobile ? 80 : 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {isMobile
                              ? groupLabel.length > 10
                                ? `${groupLabel.slice(0, 10)}...`
                                : groupLabel
                              : groupLabel.length > 20
                                ? `${groupLabel.slice(0, 20)}...`
                                : groupLabel}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Flex>
            );
          })()}

          {token && !segmentLess && (
            <Flex align="center" justify="center" style={{ flex: 1, minWidth: 0 }}>
              <Flex align="center" justify="center" className={styles[`${component}__header-segmented`]}>
                <Segmented
                  options={segmentedOptions}
                  // Clinical Practice is highlighted whenever either of its
                  // subtabs (Dashboard or Template Gallery) is the active view.
                  value={
                    selectedView === ViewType.DASHBOARD || selectedView === ViewType.TEMPLATE
                      ? ViewType.CLINICAL_PRACTICE
                      : selectedView
                  }
                  onChange={setupSelectedView}
                  disabled={isRecording || isPaused}
                />
              </Flex>
              <Dropdown
                trigger={["click"]}
                disabled={isRecording || isPaused}
                menu={{
                  items: segmentedOptions.map((opt) => ({ key: opt.value, label: opt.value })),
                  selectedKeys: [selectedView],
                  onClick: ({ key }) => setupSelectedView(key),
                }}
                overlayStyle={{ zIndex: 1101 }}
              >
                <Button className={styles[`${component}__header-nav-button`]} disabled={isRecording || isPaused}>
                  <span className={styles[`${component}__header-nav-button-label`]}>{selectedView}</span>
                  <DownOutlined style={{ fontSize: 10 }} />
                </Button>
              </Dropdown>
            </Flex>
          )}

          {token && location.pathname !== "/onboarding" && !onlyHeader && (
            <Flex align="center" gap={8} justify="flex-end" style={{ flex: 1 }}>
              {!isCompactView && (
                <>
                  <Button
                    style={{ paddingInline: 8 }}
                    onClick={() => window.open("https://getnudgeai.com/release-notes", "_blank")}
                  >
                    New Features
                  </Button>
                  <Button
                    style={{ paddingInline: 8 }}
                    icon={
                      <YoutubeFilled
                        style={{
                          color: "#ff0000",
                        }}
                      />
                    }
                    onClick={() =>
                      window.open(
                        "https://youtube.com/playlist?list=PL9lb9UfUiaJxz04wfQuQ0b8Khxln5HN-r&si=2tekWXkkfeoZIBnI",
                        "_blank",
                      )
                    }
                  >
                    Tutorials
                  </Button>
                </>
              )}
              {(!userInfo.is_paying || userInfo.group_name) && (
                <Button style={{ paddingInline: 8 }} icon={<QuestionCircleOutlined />} onClick={() => showIntercom()}>
                  {!isCompactView && "Support"}
                </Button>
              )}
              {!onlyHeader && (
                <Dropdown
                  menu={{ items: isLocked ? lockedItems : filteredItems }}
                  placement="bottom"
                  overlayStyle={{ zIndex: 1101 }}
                >
                  <Button
                    type="text"
                    icon={<MenuOutlined />}
                    style={{
                      width: 32,
                      height: 32,
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: isLocked ? 1101 : "auto",
                      borderRadius: "8px",
                      border: "1px solid #E5E7EB",
                      backgroundColor: "transparent",
                      boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#FAFAFC";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  />
                </Dropdown>
              )}
            </Flex>
          )}
        </Header>
        <TransferBanner />
        <BannerStack />
        <Content ref={contentRef} className={`${styles[`${component}__content`]}`}>
          <Outlet />
        </Content>
        {location.pathname !== "/onboarding" && <ExtensionBanner />}
        <BillingInactiveModal />
        <PurchaseModal isCloseable={!isLocked} />
        <Settings />
        <TransferModalsHost />
        {shareClientModalClient && (
          <ShareClientModal
            visible={shareClientModalVisible}
            onClose={handleShareModalClose}
            client={shareClientModalClient}
            onShareSuccess={() => {
              dispatch(fetchAllClients());
            }}
          />
        )}
      </Layout>
    </TransferProvider>
  );
};

export default AuthLayout;
